import { IntlShape } from 'react-intl';

import { storage } from '@/services/storage';
import { IDocDefinition } from '@/shared/models/doc-definition';
import { UploadedFile } from '@/shared/models/uploaded-file';
import { IDocUploaderForm } from '@/shared/store/doc-uploader/types';
import {
  createUploadForm,
  hasUnsentFile,
  needsNewFile,
} from '@/shared/store/doc-uploader/upload-form';

import { getBlockingExpiryError } from '../expiry-validation';

import {
  IFileFormat,
  ISupplementaryDocsGroup,
  ISupplementaryQuestion,
  TQuestionAnswer,
} from './types';

interface ISupplementaryDoc<TDocType extends string = string> {
  question: ISupplementaryQuestion;
  definition: IDocDefinition<TDocType>;
  form: IDocUploaderForm<TDocType>;
  answer?: TQuestionAnswer;
  storageKey?: string;
}

const STORED_ANSWERS: Record<string, TQuestionAnswer> = { '1': 'yes', '0': 'no' };

const readStoredAnswer = (storageKey: string) => STORED_ANSWERS[storage.getItem(storageKey) ?? ''];

// documents without a backend definition are not shown at all
const createSupplementaryDocs = <TDocType extends string>(
  group: ISupplementaryDocsGroup | undefined,
  docDefinitions: IDocDefinition<TDocType>[],
  uploadedDocs: Partial<Record<TDocType, UploadedFile<TDocType>>>,
  holderId: string | undefined,
): ISupplementaryDoc<TDocType>[] =>
  (group?.questions ?? []).flatMap(question => {
    const definition = docDefinitions.find(({ docType }) => docType === question.docType);

    if (!definition) {
      return [];
    }

    const upload = uploadedDocs[definition.docType];
    const isHiddenUpload =
      question.silentReview && (upload?.status === 'rejected' || upload?.status === 'expired');
    const uploadedDoc = isHiddenUpload ? undefined : upload;
    const form = createUploadForm(definition.docType, uploadedDoc);

    if (!question.ask) {
      return [{ question, definition, form }];
    }

    const storageKey = `${question.ask.storageKey}_${holderId}`;

    return [
      {
        question,
        definition,
        storageKey,
        form,
        answer: uploadedDoc ? question.ask.uploadOn : readStoredAnswer(storageKey),
      },
    ];
  });

const storeSupplementaryAnswer = <TDocType extends string>(
  { storageKey }: ISupplementaryDoc<TDocType>,
  answer: TQuestionAnswer,
) => storageKey && storage.setItem(storageKey, answer === 'yes' ? '1' : '0');

const isAllowedFile = (fileFormat: IFileFormat | undefined, file: File | undefined) =>
  !fileFormat ||
  !file ||
  fileFormat.extensions.some(extension => file.name.toLowerCase().endsWith(extension));

const isAnswered = <TDocType extends string>({ question, answer }: ISupplementaryDoc<TDocType>) =>
  !question.ask || answer !== undefined;

const isUploadRequested = <TDocType extends string>({
  answer,
  question,
}: ISupplementaryDoc<TDocType>) => !question.ask || answer === question.ask.uploadOn;

const isSupplementaryDocValid = <TDocType extends string>(
  intl: IntlShape,
  doc: ISupplementaryDoc<TDocType>,
) => {
  const { question, answer, form, definition } = doc;
  const expiryError = getBlockingExpiryError(intl, definition.isExpires, form);
  const hasValidFile = Boolean(form.file) && !needsNewFile(form) && !expiryError;

  if (!question.ask) {
    // an optional one blocks saving only with a broken new file
    return question.required ? hasValidFile : !hasUnsentFile(form) || !expiryError;
  }

  if (answer === undefined) {
    return false;
  }

  if (!isUploadRequested(doc)) {
    return true;
  }

  return hasValidFile;
};

const getSupplementaryFormsToSubmit = <TDocType extends string>(
  docs: ISupplementaryDoc<TDocType>[],
) => docs.filter(doc => isUploadRequested(doc) && hasUnsentFile(doc.form)).map(({ form }) => form);

export type { TQuestionAnswer, ISupplementaryDoc };
export {
  createSupplementaryDocs,
  storeSupplementaryAnswer,
  isAllowedFile,
  isAnswered,
  isUploadRequested,
  isSupplementaryDocValid,
  getSupplementaryFormsToSubmit,
};
